import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { formatPrice } from "@/lib/format";
import { Plus, Pencil, Eye, Search } from "lucide-react";
import { useState, useMemo } from "react";
import { STATUSES } from "@/lib/properties";
import { fetchAllRows } from "@/lib/paginate";
import { BulkTranslate } from "@/components/admin/BulkTranslate";


export const Route = createFileRoute("/_authenticated/admin/propiedades/")({
  head: () => ({ meta: [{ title: "Propiedades — Admin" }] }),
  component: PropiedadesList,
});

function PropiedadesList() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<string>("");

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admin", "properties"],
    queryFn: () =>
      fetchAllRows((from, to) =>
        supabase
          .from("properties")
          .select("id, title, slug, operation, property_type, status, price_amount, price_currency, address_text, is_featured, updated_at, views_count")
          .order("updated_at", { ascending: false })
          .range(from, to),
      ),
  });

  const filtered = useMemo(() => {
    return (data ?? []).filter((p) => {
      if (status && p.status !== status) return false;
      if (q) {
        const s = q.toLowerCase();
        return (
          p.title.toLowerCase().includes(s) ||
          p.slug.toLowerCase().includes(s) ||
          (p.address_text ?? "").toLowerCase().includes(s)
        );
      }
      return true;
    });
  }, [data, q, status]);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Propiedades</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {data?.length ?? 0} propiedades en la cartera.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <BulkTranslate kind="property" />
          <Link
            to="/admin/propiedades/nueva"
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-sm transition hover:brightness-110"
          >
            <Plus className="h-4 w-4" /> Nueva propiedad
          </Link>
        </div>
      </div>


      <div className="mt-6 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por título, slug o dirección"
            className="w-full rounded-lg border border-input bg-background pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <select
          aria-label="Estado"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="rounded-lg border border-input bg-background px-3 py-2 text-sm"
        >
          <option value="">Todos los estados</option>
          {STATUSES.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>

      <div className="mt-6 overflow-hidden rounded-xl border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-secondary/60 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Propiedad</th>
              <th className="px-4 py-3">Operación</th>
              <th className="px-4 py-3">Precio</th>
              <th className="px-4 py-3">Estado</th>
              <th className="px-4 py-3">Vistas</th>
              <th className="px-4 py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {isLoading && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">Cargando…</td></tr>
            )}
            {!isLoading && filtered.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                {data?.length ? "No hay resultados con esos filtros." : "Aún no hay propiedades. Creá la primera."}
              </td></tr>
            )}
            {filtered.map((p) => (
              <tr key={p.id} className="hover:bg-secondary/30">
                <td className="px-4 py-3">
                  <div className="font-medium text-foreground">{p.title}</div>
                  <div className="text-xs text-muted-foreground">
                    {p.property_type} · {p.address_text || p.slug}
                  </div>
                </td>
                <td className="px-4 py-3 capitalize text-muted-foreground">{p.operation.replace("_", " ")}</td>
                <td className="px-4 py-3 font-medium">
                  {formatPrice(p.price_amount, p.price_currency as "USD" | "PYG")}
                </td>
                <td className="px-4 py-3">
                  <StatusBadge status={p.status} />
                </td>
                <td className="px-4 py-3 text-muted-foreground">{p.views_count}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    <a
                      href={`/propiedad/${p.slug}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
                      title="Ver en el sitio"
                    >
                      <Eye className="h-4 w-4" />
                    </a>
                    <Link
                      to="/admin/propiedades/$id"
                      params={{ id: p.id }}
                      className="inline-flex h-8 items-center gap-1.5 rounded-md bg-secondary px-3 text-xs font-medium hover:bg-secondary/70"
                    >
                      <Pencil className="h-3.5 w-3.5" /> Editar
                    </Link>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* refetch touch to avoid unused warning */}
      <button className="hidden" onClick={() => refetch()} />
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const label = STATUSES.find((s) => s.value === status)?.label ?? status;
  const tone: Record<string, string> = {
    publicado: "bg-emerald-100 text-emerald-800",
    borrador: "bg-secondary text-secondary-foreground",
    reservado: "bg-amber-100 text-amber-800",
    vendido: "bg-slate-200 text-slate-700",
    pausado: "bg-rose-100 text-rose-800",
  };
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${tone[status] ?? "bg-secondary"}`}>
      {label}
    </span>
  );
}
